import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
} from "recharts";

interface DayCalorie {
  day: number;
  intake: number;
  burned: number;
}

interface MonthlyCalorieComparisonProps {
  data?: DayCalorie[];
  daysInMonth?: number;
}

const buildDefault = (n: number): DayCalorie[] =>
  Array.from({ length: n }, (_, i) => ({
    day: i + 1,
    intake: 2600 - Math.abs(i - 15) * 40 + (i % 3) * 60,
    burned: 900 + (i % 4) * 80,
  }));

const formatK = (v: number) => `${(v / 1000).toFixed(1).replace(/\.0$/, "")}k`;

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-[6px] px-3 py-2 shadow text-xs font-inter">
      <p className="font-semibold text-[#1F1F1F] mb-1">{label}</p>
      {payload.map((p: any) => {
        const color = p.name === "burned" ? "#D9B800" : p.color;
        return (
          <div key={p.name} className="flex items-center gap-1.5 mt-0.5">
            <span
              className="w-2 h-2 rounded-full inline-block flex-shrink-0"
              style={{ background: color }}
            />
            <span style={{ color }} className="font-medium">
              {p.name === "intake" ? "Calorie intake" : "Calorie burned"} · {p.value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

function computeCalorieYAxis(data: DayCalorie[]): { domain: [number, number]; ticks: number[] } {
  const maxVal = data.reduce(
    (m, d) => Math.max(m, d.intake ?? 0, d.burned ?? 0),
    0,
  );
  if (maxVal <= 0) return { domain: [0, 400], ticks: [0, 100, 200, 300, 400] };
  const rawStep = maxVal / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const step = Math.max(100, Math.ceil(rawStep / magnitude) * magnitude);
  const domainMax = step * 4;
  const ticks = Array.from({ length: 5 }, (_, i) => i * step);
  return { domain: [0, domainMax], ticks };
}

const MonthlyCalorieComparison: React.FC<MonthlyCalorieComparisonProps> = ({
  data,
  daysInMonth = 30,
}) => {
  const chartData = data ?? buildDefault(daysInMonth);
  const { domain, ticks } = computeCalorieYAxis(chartData);

  return (
    <div className="bg-white border border-[#F3F4F6] rounded-xl p-4 flex flex-col h-full">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[12px] 3xl:text-[14px] font-semibold uppercase tracking-widest text-[#1F1F1F] font-inter">
          Calorie Comparison
        </span>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#FF9130] inline-block" />
            <span className="text-[10px] 3xl:text-[12px] text-[#1F1F1F] font-inter">
              Calorie intake
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-[#FFE448] inline-block" />
            <span className="text-[10px] 3xl:text-[12px] text-[#1F1F1F] font-inter">
              Calorie burned
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1">
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={chartData} margin={{ top: 12, right: 12, left: -12, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#F3F4F6" />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#E5E7EB", strokeDasharray: "3 3" }} />
            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              interval={0}
              tick={{ fontSize: 10, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 11, fontFamily: "Inter, sans-serif", fill: "#8D8D8D" }}
              tickFormatter={formatK}
              domain={domain}
              ticks={ticks}
            />
            <Line
              type="monotone"
              dataKey="intake"
              stroke="#FF9130"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
            <Line
              type="monotone"
              dataKey="burned"
              stroke="#FFE448"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default MonthlyCalorieComparison;
